import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { 
  Package, 
  Search, 
  Plus,
  Edit2,
  Trash2,
  Download,
  RefreshCw,
  AlertCircle,
  X,
  Save,
  DollarSign,
  Users,
  TrendingDown,
  Info,
  ChevronDown,
  ChevronUp,
  CheckCircle
} from 'lucide-react';

const GET_ALL_PRODUCTS = gql`
  query GetProducts($category: String, $available: Boolean, $approvalStatus: String, $limit: Int, $offset: Int) {
    products(category: $category, available: $available, approvalStatus: $approvalStatus, limit: $limit, offset: $offset) {
      id
      name
      description
      category
      basePrice
      currentPrice
      approvalStatus
      priceTiers {
        groupSize
        price
      }
      unit
      quantity
      available
      vendor {
        id
        name
      }
      groups {
        id
        name
        currentSize
      }
      createdAt
      updatedAt
    }
  }
`;


const CREATE_PRODUCT = gql`
  mutation CreateProduct($input: CreateProductInput!) {
    createProduct(input: $input) {
      id
      name
    }
  }
`;

const UPDATE_PRODUCT = gql`
  mutation UpdateProduct($id: ID!, $input: UpdateProductInput!) {
    updateProduct(id: $id, input: $input) {
      id
      name
    }
  }
`;

const APPROVE_PRODUCT = gql`
  mutation ApproveProduct($productId: ID!) {
    approveProduct(productId: $productId) {
      success
      message
      product {
        id
        approvalStatus
      }
    }
  }
`;

const REJECT_PRODUCT = gql`
  mutation RejectProduct($productId: ID!, $reason: String) {
    rejectProduct(productId: $productId, reason: $reason) {
      success
      message
    }
  }
`;

const AdminProducts = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterApprovalStatus, setFilterApprovalStatus] = useState('ALL'); 
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [editFormData, setEditFormData] = useState({
    priceTiers: []
  });

  const { loading, error, data, refetch } = useQuery(GET_ALL_PRODUCTS, {
    variables: {
      available: filterStatus === 'active' ? true : filterStatus === 'inactive' ? false : undefined,
      approvalStatus: filterApprovalStatus || 'ALL',
      limit: 100,
      offset: 0
    }
  });

  const [createProduct] = useMutation(CREATE_PRODUCT, {
    onCompleted: () => {
      setShowAddModal(false);
      refetch();
    }
  });

  const [updateProduct] = useMutation(UPDATE_PRODUCT, {
    onCompleted: () => {
      setShowEditModal(false);
      refetch();
    }
  });

  const [approveProduct] = useMutation(APPROVE_PRODUCT, {
    onCompleted: () => refetch()
  });

  const [rejectProduct] = useMutation(REJECT_PRODUCT, {
    onCompleted: () => refetch()
  });

  const products = data?.products || [];

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          product.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'ALL' || product.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Calculate stats
  const stats = {
    total: products.length,
    active: products.filter(p => p.available).length,
    withGroups: products.filter(p => p.groups && p.groups.length > 0).length,
    avgDiscount: products.reduce((sum, p) => {
      if (p.basePrice && p.currentPrice) {
        return sum + ((p.basePrice - p.currentPrice) / p.basePrice * 100);
      }
      return sum;
    }, 0) / (products.length || 1)
  };

  const toggleProductExpansion = (productId) => {
    const newExpanded = new Set(expandedProducts);
    if (newExpanded.has(productId)) {
      newExpanded.delete(productId);
    } else {
      newExpanded.add(productId);
    }
    setExpandedProducts(newExpanded);
  };

  const handleAddProduct = async () => {
    try {
      await createProduct({
        variables: {
          input: {
            name: editFormData.name,
            description: editFormData.description,
            category: editFormData.category,
            basePrice: parseFloat(editFormData.basePrice),
            priceTiers: editFormData.priceTiers.map(tier => ({
              groupSize: parseInt(tier.groupSize),
              price: parseFloat(tier.price)
            })),
            unit: editFormData.unit,
            quantity: parseInt(editFormData.quantity),
            available: editFormData.available !== false
          }
        }
      });
    } catch (err) {
      console.error('Error creating product:', err);
    }
  };

  const handleEditProduct = (product) => {
    setEditFormData({
      ...product,
      priceTiers: product.priceTiers || []
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      await updateProduct({
        variables: {
          id: editFormData.id,
          input: {
            name: editFormData.name,
            description: editFormData.description,
            category: editFormData.category,
            basePrice: parseFloat(editFormData.basePrice),
            priceTiers: editFormData.priceTiers.map(tier => ({
              groupSize: parseInt(tier.groupSize),
              price: parseFloat(tier.price)
            })),
            unit: editFormData.unit,
            quantity: parseInt(editFormData.quantity),
            available: editFormData.available
          }
        }
      });
    } catch (err) {
      console.error('Error updating product:', err);
    }
  };

  const addPriceTier = () => {
    setEditFormData({
      ...editFormData,
      priceTiers: [...editFormData.priceTiers, { groupSize: '', price: '' }]
    });
  };

  const removePriceTier = (index) => {
    const newTiers = editFormData.priceTiers.filter((_, i) => i !== index);
    setEditFormData({
      ...editFormData,
      priceTiers: newTiers
    });
  };

  const updatePriceTier = (index, field, value) => {
    const newTiers = [...editFormData.priceTiers];
    newTiers[index][field] = value;
    setEditFormData({
      ...editFormData,
      priceTiers: newTiers
    });
  };

  // Product Modal Component
  const ProductModal = ({ isEdit }) => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
        <div className="bg-white rounded-lg p-6 max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">{isEdit ? 'Edit Product' : 'Add New Product'}</h2>
            <button 
              onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)} 
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="col-span-2">
              <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Product Name *</label>
                  <input
                    type="text"
                    value={editFormData.name || ''}
                    onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., Organic Tomatoes"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Category *</label>
                  <select
                    value={editFormData.category || ''}
                    onChange={(e) => setEditFormData({...editFormData, category: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">Select Category</option>
                    <option value="Produce">Produce</option>
                    <option value="Bakery">Bakery</option>
                    <option value="Pantry">Pantry</option>
                    <option value="Dairy">Dairy</option>
                    <option value="Meat">Meat</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={editFormData.description || ''}
                  onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="3"
                  placeholder="Product description..."
                />
              </div>
            </div>

            {/* Inventory & Pricing */}
            <div className="col-span-2">
              <h3 className="text-lg font-semibold mb-4">Inventory & Base Pricing</h3>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Base Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editFormData.basePrice || ''}
                    onChange={(e) => setEditFormData({...editFormData, basePrice: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Quantity *</label>
                  <input
                    type="number"
                    value={editFormData.quantity || ''}
                    onChange={(e) => setEditFormData({...editFormData, quantity: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Unit *</label>
                  <input
                    type="text"
                    value={editFormData.unit || ''}
                    onChange={(e) => setEditFormData({...editFormData, unit: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., lb, kg, each"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Status</label>
                  <select
                    value={editFormData.available !== false ? 'true' : 'false'}
                    onChange={(e) => setEditFormData({...editFormData, available: e.target.value === 'true'})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="true">Available</option>
                    <option value="false">Unavailable</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Dynamic Pricing Tiers */}
            <div className="col-span-2">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Group Pricing Tiers</h3>
                <button
                  onClick={addPriceTier}
                  className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
                >
                  <Plus size={16} />
                  Add Tier
                </button>
              </div>
              
              {editFormData.priceTiers && editFormData.priceTiers.length > 0 ? (
                <div className="space-y-2">
                  {editFormData.priceTiers.map((tier, index) => (
                    <div key={index} className="flex gap-4 items-center bg-gray-50 p-3 rounded">
                      <div className="flex-1">
                        <label className="block text-xs font-medium mb-1">Min Group Size</label>
                        <input
                          type="number"
                          value={tier.groupSize || ''}
                          onChange={(e) => updatePriceTier(index, 'groupSize', e.target.value)}
                          className="w-full px-2 py-1 border rounded"
                          placeholder="e.g., 10"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-medium mb-1">Price at this tier</label>
                        <input
                          type="number"
                          step="0.01"
                          value={tier.price || ''}
                          onChange={(e) => updatePriceTier(index, 'price', e.target.value)}
                          className="w-full px-2 py-1 border rounded"
                          placeholder="0.00"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-medium mb-1">Savings</label>
                        <div className="text-sm text-green-600 font-medium">
                          {editFormData.basePrice && tier.price ? 
                            `${((editFormData.basePrice - tier.price) / editFormData.basePrice * 100).toFixed(1)}% off` 
                            : '-'
                          }
                        </div>
                      </div>
                      <button
                        onClick={() => removePriceTier(index)}
                        className="text-red-500 hover:text-red-700 mt-4"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded">
                  <TrendingDown size={32} className="mx-auto mb-2 text-gray-400" />
                  <p className="text-sm text-gray-600">No pricing tiers defined</p>
                  <p className="text-xs text-gray-500 mt-1">Add tiers to enable group discounts</p>
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button 
              onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button 
              onClick={isEdit ? handleSaveEdit : handleAddProduct}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Save size={16} />
              {isEdit ? 'Save Changes' : 'Create Product'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Product Management</h1>
            <p className="text-gray-600">Manage products and dynamic pricing tiers</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => refetch()}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw size={20} />
            </button>
            {/* BULK APPROVE BUTTON - ADD THIS ENTIRE SECTION */}
            {products.filter(p => p.approvalStatus === 'pending').length > 0 && (
              <button
                onClick={async () => {
                  if (window.confirm(`Approve all ${products.filter(p => p.approvalStatus === 'pending').length} pending products?`)) {
                    const pendingProducts = products.filter(p => p.approvalStatus === 'pending');
                    for (const product of pendingProducts) {
                      await approveProduct({ variables: { productId: product.id }});
                    }
                    refetch();
                  } 
                }}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <CheckCircle size={20} />
                Approve All ({products.filter(p => p.approvalStatus === 'pending').length})
              </button>
            )}
            {/* END OF BULK APPROVE BUTTON */}

            <button
              onClick={() => {
                setEditFormData({ priceTiers: [] });
                setShowAddModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus size={20} />
              Add Product
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-sm text-gray-600">Total Products</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
            <div className="text-sm text-gray-600">Available</div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{stats.withGroups}</div>
            <div className="text-sm text-gray-600">In Groups</div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">{stats.avgDiscount.toFixed(1)}%</div>
            <div className="text-sm text-gray-600">Avg Discount</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        {/* Approval Status Tabs */}
        <div className="flex gap-2 mb-4 border-b">
          <button
            onClick={() => setFilterApprovalStatus('ALL')}
            className={`px-4 py-2 ${filterApprovalStatus === 'ALL' ? 'border-b-2 border-blue-600 font-semibold' : ''}`}
          >
            All ({products.length})
          </button>
          <button
            onClick={() => setFilterApprovalStatus('pending')}
            className={`px-4 py-2 ${filterApprovalStatus === 'pending' ? 'border-b-2 border-yellow-600 font-semibold text-yellow-600' : ''}`}
          >
            Pending ({products.filter(p => p.approvalStatus === 'pending').length})
          </button>
          <button
            onClick={() => setFilterApprovalStatus('approved')}
            className={`px-4 py-2 ${filterApprovalStatus === 'approved' ? 'border-b-2 border-green-600 font-semibold text-green-600' : ''}`}
          >
            Approved ({products.filter(p => p.approvalStatus === 'approved').length})
          </button>
          <button
            onClick={() => setFilterApprovalStatus('rejected')}
            className={`px-4 py-2 ${filterApprovalStatus === 'rejected' ? 'border-b-2 border-red-600 font-semibold text-red-600' : ''}`}
          >
            Rejected ({products.filter(p => p.approvalStatus === 'rejected').length})
          </button>
        </div>
        <div className="flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="ALL">All Categories</option>
            <option value="Produce">Produce</option>
            <option value="Bakery">Bakery</option>
            <option value="Pantry">Pantry</option>
            <option value="Dairy">Dairy</option>
            <option value="Meat">Meat</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>

      {/* Products List */}
      <div className="bg-white rounded-lg shadow-sm">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Product
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Base Price
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Current Price
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Groups
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Approval Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredProducts.map((product) => {
              const isExpanded = expandedProducts.has(product.id);
              
              return (
                <React.Fragment key={product.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{product.name}</div>
                        <div className="text-sm text-gray-500">{product.category}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">${product.basePrice?.toFixed(2)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium">${product.currentPrice?.toFixed(2)}</div>
                        {product.basePrice && product.currentPrice < product.basePrice && (
                          <div className="text-xs text-green-600">
                            {((product.basePrice - product.currentPrice) / product.basePrice * 100).toFixed(0)}% off
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Users size={16} className="mr-1 text-gray-400" />
                        <span className="text-sm">{product.groups?.length || 0}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        product.available ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {product.available ? 'Available' : 'Unavailable'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        product.approvalStatus === 'approved' ? 'bg-green-100 text-green-800' :
                        product.approvalStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {product.approvalStatus || 'Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex gap-2">
                        {product.approvalStatus === 'pending' && (
                          <>
                            <button
                              onClick={() => approveProduct({ variables: { productId: product.id }})}
                              className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => rejectProduct({ variables: { productId: product.id, reason: 'Needs review' }})}
                              className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => toggleProductExpansion(product.id)}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                        <button
                          onClick={() => handleEditProduct(product)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  
                  {isExpanded && (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 bg-gray-50">
                        <div className="grid grid-cols-3 gap-6">
                          <div>
                            <h4 className="font-medium mb-2">Product Details</h4>
                            <div className="space-y-1 text-sm">
                              <p><span className="font-medium">Description:</span> {product.description || 'No description'}</p>
                              <p><span className="font-medium">Vendor:</span> {product.vendor?.name || 'Unknown'}</p>
                              <p><span className="font-medium">Inventory:</span> {product.quantity} {product.unit}</p>
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="font-medium mb-2">Pricing Tiers</h4>
                            {product.priceTiers && product.priceTiers.length > 0 ? (
                              <div className="space-y-1 text-sm">
                                {product.priceTiers.map((tier, idx) => (
                                  <div key={idx} className="flex justify-between">
                                    <span>{tier.groupSize}+ members:</span>
                                    <span className="font-medium">${tier.price.toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">No tiers defined</p>
                            )}
                          </div>
                          
                          <div>
                            <h4 className="font-medium mb-2">Active Groups</h4>
                            {product.groups && product.groups.length > 0 ? (
                              <div className="space-y-1">
                                {product.groups.map(group => (
                                  <div key={group.id} className="text-sm">
                                    <span>{group.name}</span>
                                    <span className="text-gray-500 ml-2">({group.currentSize} members)</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">Not in any groups</p>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {showAddModal && <ProductModal isEdit={false} />}
      {showEditModal && <ProductModal isEdit={true} />}
    </div>
  );
};

export default AdminProducts;
